import { FileTypes } from "../models/constants/file-types";
import { PayerRiskAdjustmentPolicy } from "../models/payer-config-item";

// Shared display/validation rules for payer service year risk policies, used by the payer config
// grid and the payer / risk policy dialogs.
export class PayerPolicyFunctions {

  // Service year dates come from the API as display strings (M/d/yyyy).
  static toDate(value?: string | Date | null): Date | null {
    if (!value) {
      return null;
    }

    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }

  static receivesFileType(policy: PayerRiskAdjustmentPolicy | null | undefined, fileType: FileTypes): boolean {
    return !!policy?.FileSubTypesByFileType?.some(ft => ft.FileTypeID === (fileType as number));
  }

  static asmTemplateIds(policy?: PayerRiskAdjustmentPolicy | null): string[] {
    return policy?.ASMTemplateIDs
      ? policy.ASMTemplateIDs
          .split(/\s*,\s*/)
          .map(id => id.trim())
          .filter(id => id.length > 0)
      : [];
  }

  // Only ASM templates can be assigned to a policy. Payer validation templates are ignored, since
  // legacy policies may still carry one.
  static templateCount(policy?: PayerRiskAdjustmentPolicy | null): number {
    return this.asmTemplateIds(policy).length;
  }

  static templateNames(policy?: PayerRiskAdjustmentPolicy | null): string {
    return policy?.ASMTemplateNames ?? '';
  }

  // Inclusive range overlap; false when either range is incomplete.
  static rangesOverlap(
    aStart: string | Date | null | undefined, aEnd: string | Date | null | undefined,
    bStart: string | Date | null | undefined, bEnd: string | Date | null | undefined
  ): boolean {
    const [as, ae, bs, be] = [aStart, aEnd, bStart, bEnd].map(v => this.toDate(v));
    if (!as || !ae || !bs || !be) {
      return false;
    }

    return as <= be && ae >= bs;
  }

  // First of the payer's other policies whose service years overlap the given range.
  static findOverlappingPolicy(
    start: string | Date | null | undefined,
    end: string | Date | null | undefined,
    policies: PayerRiskAdjustmentPolicy[] | null | undefined,
    excludePolicyId?: number | null
  ): PayerRiskAdjustmentPolicy | undefined {
    return (policies ?? []).find(p =>
      p.PolicyID !== excludePolicyId &&
      this.rangesOverlap(start, end, p.ServiceYearStart, p.ServiceYearEnd));
  }
}
